import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { toast } from "sonner";
import { Search, UserPlus, X } from "lucide-react";

// Componente UNICO di riconciliazione studio YouPAM. Due modalità: "Trova paziente iscritto" (associazione autorizzata
// con relazione clinica attiva) e "Invita nuovo paziente" (riusa gli inviti clinici esistenti, mantenendo il legame
// con la scheda interna/studio). Finestra centrata, stabile e responsive. Nessuna associazione su dati non verificati.
export function ReconcileStudy({ study, onClose, onDone }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("find");
  const [q, setQ] = useState({ first_name: "", last_name: "", fiscal_code: "" });
  const [res, setRes] = useState([]);
  const [inv, setInv] = useState({ first_name: "", last_name: "", tax_id: "", email: "" });
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if ((q.fiscal_code || "").replace(/\s/g, "").length < 11 || !q.first_name.trim() || !q.last_name.trim()) { toast.message(t("diag.search_hint")); return; }
    try {
      const { data } = await api.get("/clinical/patient-search", { params: { first_name: q.first_name, last_name: q.last_name, tax_id: q.fiscal_code } });
      setRes(data.found && data.patient ? [data.patient] : []);
    } catch (e) { toast.error(e.response?.status === 400 ? t("diag.search_hint") : t("diag.search_err")); }
  };
  const associate = async (patientId) => {
    setBusy(true);
    try { await api.post(`/pam-connect/unmatched/${study.ingest_id}/associate`, { patient_id: patientId });
      toast.success(t("diag.assoc_ok")); onDone && onDone(); onClose(); }
    catch (e) { toast.error(e.response?.status === 403 ? t("diag.assoc_rel_err") : t("diag.assoc_err")); }
    finally { setBusy(false); }
  };
  const invite = async () => {
    if (!inv.first_name.trim() || !inv.last_name.trim() || (inv.tax_id || "").replace(/\s/g, "").length < 11 || !inv.email.trim()) { toast.message(t("diag.invite_fields")); return; }
    if (!study.internal_patient_id) { toast.error(t("diag.assoc_err")); return; }
    setBusy(true);
    try { await api.post(`/pam-connect/internal-patients/${study.internal_patient_id}/invite`, { first_name: inv.first_name, last_name: inv.last_name, tax_id: inv.tax_id, email: inv.email });
      toast.success(t("diag.invite_sent")); onDone && onDone(); onClose(); }
    catch { toast.error(t("diag.assoc_err")); }
    finally { setBusy(false); }
  };

  const tabCls = (active) => `flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;

  return (<div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="reconcile-overlay">
    <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="reconcile-modal">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-lg text-slate-900">{t("diag.rec_title")}</h3>
        <button className="text-slate-400 hover:text-slate-700" onClick={onClose} data-testid="reconcile-close"><X size={18} /></button>
      </div>
      <p className="text-xs text-slate-500 mb-4">{study.modality || "DICOM"} · {study.dicom_patient_name || t("diag.unknown_patient")} · {t("diag.c_accession")} {study.accession_number || "—"}</p>

      <div className="flex gap-2 mb-4">
        <button className={tabCls(tab === "find")} onClick={() => setTab("find")} data-testid="rec-tab-find"><Search size={15} />{t("diag.find_patient")}</button>
        <button className={tabCls(tab === "invite")} onClick={() => setTab("invite")} data-testid="rec-tab-invite"><UserPlus size={15} />{t("diag.rec_tab_invite")}</button>
      </div>

      {tab === "find" && <div data-testid="rec-find-panel">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <input className="yp-input !py-1.5 text-sm" autoComplete="off" placeholder={t("diag.first_name")} value={q.first_name} onChange={(e) => setQ({ ...q, first_name: e.target.value })} data-testid="rec-q-first" />
          <input className="yp-input !py-1.5 text-sm" autoComplete="off" placeholder={t("diag.last_name")} value={q.last_name} onChange={(e) => setQ({ ...q, last_name: e.target.value })} data-testid="rec-q-last" />
          <input className="yp-input !py-1.5 text-sm" autoComplete="off" placeholder={t("diag.fiscal_code")} value={q.fiscal_code} onChange={(e) => setQ({ ...q, fiscal_code: e.target.value })} data-testid="rec-q-cf" />
        </div>
        <button className="yp-btn yp-btn-primary !py-1.5 !px-3 text-sm w-full mb-3" onClick={search} data-testid="rec-search-btn">{t("diag.search")}</button>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {res.map((p) => (
            <div key={p.id || p.patient_id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
              <span>{p.first_name} {p.last_name}</span>
              <button className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs text-blue-600" disabled={busy} onClick={() => associate(p.id || p.patient_id)} data-testid="rec-pick-btn">{t("diag.link")}</button>
            </div>))}
          {!res.length && <div className="text-xs text-slate-400">{t("diag.no_results")}</div>}
        </div>
      </div>}

      {tab === "invite" && <div data-testid="rec-invite-panel">
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input className="yp-input !py-1.5 text-sm" autoComplete="off" placeholder={t("diag.first_name")} value={inv.first_name} onChange={(e) => setInv({ ...inv, first_name: e.target.value })} data-testid="rec-inv-first" />
          <input className="yp-input !py-1.5 text-sm" autoComplete="off" placeholder={t("diag.last_name")} value={inv.last_name} onChange={(e) => setInv({ ...inv, last_name: e.target.value })} data-testid="rec-inv-last" />
          <input className="yp-input !py-1.5 text-sm" autoComplete="off" placeholder={t("diag.tax_id")} value={inv.tax_id} onChange={(e) => setInv({ ...inv, tax_id: e.target.value })} data-testid="rec-inv-cf" />
          <input className="yp-input !py-1.5 text-sm" type="email" autoComplete="off" placeholder={t("diag.email")} value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} data-testid="rec-inv-email" />
        </div>
        <button className="yp-btn yp-btn-primary !py-1.5 !px-3 text-sm w-full" disabled={busy} onClick={invite} data-testid="rec-invite-btn">{t("diag.invite_cta")}</button>
      </div>}
    </div>
  </div>);
}
